import { Button, useTheme, type SxProps, type Theme } from '@mui/material';
import { HXCIcon, type IconsType } from '../HXCIcon';
import { MouseEventHandler } from 'react';
import { type HealthxchangeThemeColours } from '../../theme/defaultTheme';

type LabelPlacement = 'left' | 'right' | 'top' | 'bottom' | 'none';

export interface IconButtonProps {
  iconName: IconsType;
  iconColor: keyof typeof HealthxchangeThemeColours;
  scale?: number;
  onClick: MouseEventHandler<HTMLButtonElement>;
  label: string;
  labelPlacement?: LabelPlacement;
  testId?: string;
  style?: SxProps<Theme>;
  iconSx?: SxProps<Theme>;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
}

/**
 * A button that contains an icon and optional label
 */
const IconButton: React.FC<IconButtonProps> = ({
  iconName,
  iconColor,
  scale = 1,
  onClick,
  label,
  labelPlacement = 'none',
  testId,
  style,
  iconSx,
  disabled = false,
  type = 'button',
}) => {
  const theme = useTheme();
  const isReset = type === 'reset';

  const getMarginForPlacement = (placement: LabelPlacement): string | number => {
    const margins: Record<LabelPlacement, string | number> = {
      left: '0 4px 0 0',
      top: '0 0 4px 0',
      right: '0 0 0 4px',
      bottom: '4px 0 0 0',
      none: 0,
    };
    return margins[placement];
  };

  const getLabelStyle = (placement: LabelPlacement): React.CSSProperties => {
    if (isReset) {
      return {
        margin: getMarginForPlacement(placement),
        color: theme.palette.red.main,
        fontWeight: 'normal',
        fontSize: '0.75rem',
        textDecoration: 'underline',
        textTransform: 'none',
      };
    }

    // fallback for regular variant
    return {
      margin: getMarginForPlacement(placement),
      fontWeight: 500,
      fontSize: '0.875rem',
      color: 'black',
      textTransform: 'none',
    };
  };
  const content = (
    <>
      {(labelPlacement === 'left' || labelPlacement === 'top') && (
        <span style={getLabelStyle(labelPlacement)}>{label}</span>
      )}
      <HXCIcon
        name={iconName}
        testId={`${testId}-icon`}
        color={isReset ? 'red' : iconColor}
        scale={scale}
        sx={{ verticalAlign: 'middle', ...iconSx }}
      />
      {(labelPlacement === 'right' || labelPlacement === 'bottom') && (
        <span style={getLabelStyle(labelPlacement)}>{label}</span>
      )}
    </>
  );

  return (
    <Button
      type={type}
      onClick={onClick}
      aria-label={label}
      title={label}
      data-testid={testId}
      disabled={disabled}
      sx={{
        background: 'none',
        border: 'none',
        padding: labelPlacement === 'none' ? 0 : 1,
        minWidth: labelPlacement === 'none' ? '24px' : 'auto',
        minHeight: labelPlacement === 'none' ? '24px' : 'auto',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: labelPlacement === 'top' || labelPlacement === 'bottom' ? 'column' : 'row',
        textTransform: 'none',
        '&:hover': isReset
          ? {
              backgroundColor: 'transparent',
            }
          : {
              backgroundColor: theme.palette.silver.light,
            },
        ...style,
      }}
    >
      {content}
    </Button>
  );
};

export default IconButton;
